import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  PlaylistAdd as SeedIcon,
  Warning as WarningIcon,
  AutoAwesome as IAIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../../services/api';

const CategoryManagement = () => {
  const groupGuides = [
    { group: 'Nucleo essencial', description: 'Categorias amplas e seguras para quase qualquer operação. Servem como base de cobertura e fallback.' },
    { group: 'Mercado e consumo', description: 'Categorias ligadas a público, desejo, compra e posicionamento de marca.' },
    { group: 'Corporativo e gestao', description: 'Categorias para o universo de empresas, liderança, carreira, RH e marketing.' },
    { group: 'Financas e negocios', description: 'Categorias para economia aplicada, mercado financeiro, tributos e risco.' },
    { group: 'Tecnologia e inovacao', description: 'Categorias para tecnologia, dados, automacao e evolucao digital.' },
    { group: 'Industria e infraestrutura', description: 'Categorias de producao, cadeia de valor, energia, obra e mobilidade.' },
    { group: 'Midia e conteudo', description: 'Categorias de imprensa, criadores, redes, audio e video.' },
    { group: 'Sociedade e vida publica', description: 'Categorias de governo, justica, seguranca, cidadania e meio ambiente.' },
    { group: 'Geografia e cobertura', description: 'Categorias que descrevem o alcance territorial da fonte.' },
    { group: 'Entretenimento e interesse', description: 'Categorias de cultura pop e temas de interesse recorrente.' }
  ];

  const inferGroupFromValue = (value) => {
    const normalized = String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w]/g, '')
      .toLowerCase();

    const groupMap = {
      automotivo: 'Industria e infraestrutura',
      tecnologia: 'Tecnologia e inovacao',
      economiafinancas: 'Financas e negocios',
      entretenimento: 'Nucleo essencial',
      esportes: 'Nucleo essencial',
      lifestyle: 'Mercado e consumo',
      politica: 'Sociedade e vida publica',
      saude: 'Nucleo essencial',
      educacao: 'Nucleo essencial',
      varejocomercio: 'Mercado e consumo',
      varejo: 'Mercado e consumo',
      industria: 'Industria e infraestrutura',
      logistica: 'Industria e infraestrutura',
      agronegocio: 'Industria e infraestrutura',
      imobiliario: 'Industria e infraestrutura',
      turismo: 'Mercado e consumo',
      modabeleza: 'Mercado e consumo',
      moda: 'Mercado e consumo',
      beleza: 'Mercado e consumo',
      gastronomia: 'Mercado e consumo',
      portal: 'Midia e conteudo',
      jornaisonline: 'Midia e conteudo',
      redes: 'Midia e conteudo',
      redessociais: 'Midia e conteudo',
      noticias: 'Midia e conteudo',
      geral: 'Nucleo essencial',
      outros: 'Nucleo essencial',
      geralnacional: 'Geografia e cobertura'
    };

    return groupMap[normalized] || '';
  };

  const handleCategoryFieldChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (dialogMode === 'create' && field !== 'group') {
        const inferredGroup = inferGroupFromValue(field === 'value' ? value : (next.value || next.label));
        if (inferredGroup && !prev.group) {
          next.group = inferredGroup;
        }
      }
      return next;
    });
  };

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templateCategory, setTemplateCategory] = useState(null);

  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Form state
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    description: '',
    group: '',
    icon: '',
    color: ''
  });

  const suggestedGroup = inferGroupFromValue(formData.value || formData.label);
  const seedCategories = useMemo(() => categories.filter((category) => category.isSystem), [categories]);
  const catalogByGroup = useMemo(() => {
    return categories.reduce((acc, category) => {
      const group = category.group || inferGroupFromValue(category.value) || 'Sem grupo';
      if (!acc[group]) acc[group] = [];
      acc[group].push(category);
      return acc;
    }, {});
  }, [categories]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/categories/all');
      if (res.data.success) {
        setCategories(res.data.data);
      }
      setError('');
    } catch (err) {
      setError('Erro ao carregar categorias: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenCreate = () => {
    setTemplateCategory(null);
    setFormData({
      value: '',
      label: '',
      description: '',
      group: '',
      icon: '',
      color: ''
    });
    setDialogMode('create');
    setSelectedCategory(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (category) => {
    setTemplateCategory(null);
    setFormData({
      value: category.value,
      label: category.label,
      description: category.description || '',
      group: category.group || '',
      icon: category.icon || '',
      color: category.color || ''
    });
    setDialogMode('edit');
    setSelectedCategory(category);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
    setTemplateCategory(null);
    setError('');
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  const handleTemplateSelect = (template) => {
    setTemplateCategory(template);
    if (!template) {
      setFormData({
        value: '',
        label: '',
        description: '',
        group: '',
        icon: '',
        color: ''
      });
      return;
    }

    setFormData({
      value: template.value || '',
      label: template.label || '',
      description: template.description || '',
      group: template.group || inferGroupFromValue(template.value) || '',
      icon: template.icon || '',
      color: template.color || ''
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const normalizedGroup = formData.group?.trim() || suggestedGroup || inferGroupFromValue(formData.value || formData.label);
      const payload = {
        ...formData,
        group: normalizedGroup || ''
      };

      if (dialogMode === 'create') {
        const res = await api.post('/categories', payload);
        if (res.data.success) {
          setSuccess('Categoria criada com sucesso!');
          fetchCategories();
          handleCloseDialog();
        }
      } else {
        const res = await api.put(`/categories/${selectedCategory._id}`, payload);
        if (res.data.success) {
          setSuccess('Categoria atualizada com sucesso!');
          fetchCategories();
          handleCloseDialog();
        }
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      const res = await api.put(`/categories/${category._id}/toggle`);
      if (res.data.success) {
        setSuccess(res.data.message);
        fetchCategories();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDelete = (category) => {
    setConfirmDialog({
      open: true,
      title: 'Desativar Categoria',
      message: `Deseja realmente desativar a categoria "${category.label}"? Ela podera ser reativada posteriormente.`,
      onConfirm: async () => {
        handleCloseConfirmDialog();
        try {
          const res = await api.delete(`/categories/${category._id}`);
          if (res.data.success) {
            setSuccess(res.data.message);
            fetchCategories();
            setTimeout(() => setSuccess(''), 3000);
          }
        } catch (err) {
          setError(err.response?.data?.message || 'Erro ao excluir categoria');
        }
      }
    });
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;

    const newOrder = categories.map(c => c._id);
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];

    try {
      await api.put('/categories/reorder', { orderedIds: newOrder });
      fetchCategories();
    } catch (err) {
      setError('Erro ao reordenar categorias');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === categories.length - 1) return;

    const newOrder = categories.map(c => c._id);
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];

    try {
      await api.put('/categories/reorder', { orderedIds: newOrder });
      fetchCategories();
    } catch (err) {
      setError('Erro ao reordenar categorias');
    }
  };

  const handleSeedDefaults = () => {
    setConfirmDialog({
      open: true,
      title: 'Criar Catálogo Padrão',
      message: 'Isso criara o catálogo amplo e organizado de categorias do sistema. Categorias existentes nao serao afetadas. Continuar?',
      onConfirm: async () => {
        handleCloseConfirmDialog();
        try {
          setLoading(true);
          const res = await api.post('/categories/seed');
          if (res.data.success) {
            setSuccess(res.data.message);
            fetchCategories();
            setTimeout(() => setSuccess(''), 3000);
          }
        } catch (err) {
          setError(err.response?.data?.message || 'Erro ao criar categorias padrao');
          setLoading(false);
        }
      }
    });
  };

  const filteredCategories = categories.filter(cat =>
    cat.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.group || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CategoryIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Gerenciamento de Categorias
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure categorias de conteúdo
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 1
              }}
            >
              {[
                { title: 'value', text: 'Identificador técnico usado pelo sistema e pelas integrações.' },
                { title: 'label', text: 'Nome exibido para o time e para a interface.' },
                { title: 'group', text: 'Bloco macro que ajuda a organizar a taxonomia.' }
              ].map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Chip
                    label={item.title}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700, mb: 0.75 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Accordion sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Guia rapido dos grupos
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.25 }}>
                  {groupGuides.map((item) => (
                    <Box key={item.group} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {item.group}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        {item.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Criar o catálogo padrao de categorias do sistema">
            <Button
              variant="outlined"
              startIcon={<SeedIcon />}
              onClick={handleSeedDefaults}
              disabled={loading}
            >
              Seed
            </Button>
          </Tooltip>
          <Tooltip title="Atualizar lista">
            <IconButton onClick={fetchCategories} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Nova Categoria
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar categorias..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={60}>Ordem</TableCell>
              <TableCell>Identificador</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell>Descricao</TableCell>
              <TableCell width={80} align="center">Sistema</TableCell>
              <TableCell width={80} align="center">Ativo</TableCell>
              <TableCell width={150} align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada. Clique em "Seed" para criar o catálogo padrao.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category, index) => (
                <TableRow
                  key={category._id}
                  sx={{
                    opacity: category.isActive ? 1 : 0.5,
                    backgroundColor: category.isActive ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === filteredCategories.length - 1}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {category.value}
                    </Typography>
                  </TableCell>
                  <TableCell>{category.label}</TableCell>
                  <TableCell>
                    {category.group ? (
                      <Chip label={category.group} size="small" variant="outlined" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {category.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {category.isSystem && (
                      <Chip label="Sistema" size="small" color="info" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={category.isActive}
                      onChange={() => handleToggleActive(category)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(category)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!category.isSystem && (
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(category)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Info */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Total: {categories.length} categorias ({categories.filter(c => c.isActive).length} ativas)
        </Typography>
      </Box>

      {/* Dialog Create/Edit */}
      <PlatformDialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title={dialogMode === 'create' ? 'Nova Categoria' : 'Editar Categoria'}
          subtitle={dialogMode === 'create' ? 'Cadastre uma nova taxonomia de conteúdo que alimenta marcas, fontes e Tier.' : `Ajustando: ${formData.label}`}
          icon={<CategoryIcon />}
          onClose={handleCloseDialog}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {dialogMode === 'create' && (
              <Autocomplete
                options={seedCategories}
                value={templateCategory}
                onChange={(event, newValue) => handleTemplateSelect(newValue)}
                getOptionLabel={(option) => `${option.label || option.value}${option.group ? ` • ${option.group}` : ''}`}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Base do catálogo"
                    placeholder="Escolha uma categoria existente para usar como base"
                    helperText="Isso preenche o formulário com uma categoria já padronizada do seed."
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {option.label || option.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.group || inferGroupFromValue(option.value) || 'Sem grupo'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            )}

            <TextField
              label="Identificador de Sistema (Slug)"
              size="small"
              value={formData.value}
              onChange={(e) => handleCategoryFieldChange('value', e.target.value)}
              disabled={dialogMode === 'edit'}
              required
              fullWidth
              placeholder="Ex: Automotivo, Tecnologia"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Nome de Exibição (Label)"
              size="small"
              value={formData.label}
              onChange={(e) => handleCategoryFieldChange('label', e.target.value)}
              required
              fullWidth
              placeholder="Ex: Economia & Negócios"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Descrição da Categoria"
              size="small"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              placeholder="Descreva o propósito desta categoria..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Grupo</InputLabel>
                <Select
                  value={formData.group}
                  label="Grupo"
                  onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                  sx={{ borderRadius: 2 }}
                >
                  {groupGuides.map((item) => (
                    <MenuItem key={item.group} value={item.group}>
                      {item.group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Grupo sugerido:
                </Typography>
                <Chip
                  label={suggestedGroup || 'Sem sugestão'}
                  size="small"
                  color={suggestedGroup ? 'primary' : 'default'}
                  variant={suggestedGroup ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Escolha o bloco macro da categoria. A sugestão é preenchida automaticamente pelo nome do item.
              </Typography>
              {templateCategory && (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Base selecionada: <strong>{templateCategory.label || templateCategory.value}</strong>. Você pode ajustar os campos abaixo antes de salvar.
                </Alert>
              )}
            </Box>

            <Accordion sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Catálogo disponível
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Veja os grupos e categorias já existentes antes de salvar.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {Object.entries(catalogByGroup).map(([group, groupCategories]) => (
                    <Box key={group} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {group}
                        </Typography>
                        <Chip label={`${groupCategories.length} itens`} size="small" variant="outlined" />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {groupCategories.map((category) => (
                          <Chip
                            key={category._id}
                            label={category.label || category.value}
                            size="small"
                            variant={category.isActive ? 'filled' : 'outlined'}
                            color={category.isActive ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ícone / Emoji"
                size="small"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                fullWidth
                placeholder="Ex: 🚗 ou Business"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Cor Temática"
                size="small"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                fullWidth
                placeholder="Ex: #4caf50"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={saving} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.value || !formData.label}
            sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}
          >
            {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {dialogMode === 'create' ? 'Criar Categoria' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Confirmation Dialog */}
      <PlatformDialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        maxWidth="xs"
        fullWidth
      >
        <PlatformDialogTitle 
          title={confirmDialog.title}
          subtitle="Ação requer confirmação para prosseguir."
          icon={<WarningIcon />}
          onClose={handleCloseConfirmDialog}
          error={confirmDialog.title.toLowerCase().includes('excluir') || confirmDialog.title.toLowerCase().includes('desativar')}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" fontWeight={500}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseConfirmDialog} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmDialog.onConfirm}
            autoFocus
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Confirmar Ação
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default CategoryManagement;
